import React from 'react';
import { X, Command } from 'lucide-react';
import { KEYBOARD_SHORTCUTS } from '../hooks/useKeyboardShortcuts';

/**
 * Modal displaying all available keyboard shortcuts
 */
export const KeyboardShortcutsModal = ({ isOpen, onClose }) => {
    if (!isOpen) return null;

    const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;

    const formatKey = (key) => {
        if (key === 'Ctrl/Cmd') {
            return isMac ? '⌘' : 'Ctrl';
        }
        if (key === 'Esc') return 'Esc';
        return key.toUpperCase();
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-secondary-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-secondary-200 dark:border-secondary-700">
                    <div className="flex items-center gap-3">
                        <Command className="text-primary-500" size={24} />
                        <h2 className="text-xl font-bold text-secondary-900 dark:text-white">
                            Keyboard Shortcuts
                        </h2>
                    </div>
                    <button
                        onClick={onClose}
                        data-modal-close
                        className="p-2 hover:bg-secondary-100 dark:hover:bg-secondary-700 rounded-lg transition-colors"
                        aria-label="Close"
                    >
                        <X size={20} className="text-secondary-600 dark:text-secondary-400" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)]">
                    {KEYBOARD_SHORTCUTS.map((category, idx) => (
                        <div key={idx} className={idx > 0 ? 'mt-6' : ''}>
                            <h3 className="text-sm font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider mb-3">
                                {category.category}
                            </h3>
                            <div className="space-y-2">
                                {category.shortcuts.map((shortcut, sIdx) => (
                                    <div
                                        key={sIdx}
                                        className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-secondary-50 dark:hover:bg-secondary-700/50 transition-colors"
                                    >
                                        <span className="text-secondary-700 dark:text-secondary-300">
                                            {shortcut.description}
                                        </span>
                                        <div className="flex items-center gap-1">
                                            {shortcut.keys.map((key, kIdx) => (
                                                <React.Fragment key={kIdx}>
                                                    <kbd className="px-2 py-1 text-xs font-semibold text-secondary-800 dark:text-secondary-200 bg-secondary-100 dark:bg-secondary-700 border border-secondary-300 dark:border-secondary-600 rounded shadow-sm">
                                                        {formatKey(key)}
                                                    </kbd>
                                                    {kIdx < shortcut.keys.length - 1 && (
                                                        <span className="text-secondary-400 dark:text-secondary-500 text-xs mx-1">
                                                            +
                                                        </span>
                                                    )}
                                                </React.Fragment>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Footer */}
                <div className="p-4 bg-secondary-50 dark:bg-secondary-900 border-t border-secondary-200 dark:border-secondary-700">
                    <p className="text-xs text-secondary-600 dark:text-secondary-400 text-center">
                        Press <kbd className="px-1.5 py-0.5 text-xs bg-white dark:bg-secondary-800 border border-secondary-300 dark:border-secondary-600 rounded">
                            {isMac ? '⌘' : 'Ctrl'}
                        </kbd> + <kbd className="px-1.5 py-0.5 text-xs bg-white dark:bg-secondary-800 border border-secondary-300 dark:border-secondary-600 rounded">
                            /
                        </kbd> to toggle this help
                    </p>
                </div>
            </div>
        </div>
    );
};
